import React, { useMemo, useState } from "react";
import { Tabs } from "@mantine/core";
import { useAuthUser } from "react-auth-kit";
import { PageList, ENUM_FORM_DISPLAY } from "../components/pageList";
import { IconEye, IconCircleCheck } from "@tabler/icons";
import ActionCellRender from "../components/actionCellRender";
import McaPageStats from "../components/mca/McaPageStats";
import { mcaRequestColumns } from "../components/mca/mcaTableHelpers";
import { getMcaRequestPermissions } from "../components/mca/mcaRequestPermissions";
import { useTabNavigate } from "../hooks/useTabNavigate";

const apiEntity = "mcaRequest";

const formSchema = {
  title: "MCA Request",
  apiEntity,
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "sm",
  },
  initialValues: {},
  layout: {
    fields: [],
  },
};

const McaRequestTabs = ({ tab, setTab }) => (
  <Tabs value={tab} onTabChange={setTab} mb="md">
    <Tabs.List>
      <Tabs.Tab value="pending">My Pending Approvals</Tabs.Tab>
      <Tabs.Tab value="submitted">My Submissions</Tabs.Tab>
      <Tabs.Tab value="all">All Requests</Tabs.Tab>
    </Tabs.List>
  </Tabs>
);

const McaRequest = () => {
  const tabNav = useTabNavigate();
  const auth = useAuthUser();
  const session = auth();
  const [tab, setTab] = useState("pending");

  const preQuery = useMemo(() => {
    if (tab === "pending") return { _mcaTab: "pending" };
    if (tab === "submitted") return { makerOnly: true };
    return {};
  }, [tab]);

  const emptyState = useMemo(() => {
    if (tab === "pending") {
      return { message: "No pending approval" };
    }
    if (tab === "submitted") {
      return { message: "No submissions" };
    }
    return null;
  }, [tab]);

  const openDetail = (data) => {
    const label = data.code ? `MCA - ${data.code}` : "MCA Request";
    tabNav.open({
      pathname: `/mca/requests/${data._id}`,
      label,
    });
  };

  const tableSchema = useMemo(
    () => ({
      columns: [
        ...mcaRequestColumns(),
        {
          cellRender: ActionCellRender,
          isAction: true,
          actions: [
            {
              name: "approve",
              label: "Review & approve",
              icon: IconCircleCheck,
              visible: (data) =>
                getMcaRequestPermissions(data, session).canApprove,
            },
            { name: "view", label: "View", icon: IconEye },
          ],
        },
      ],
      onActionBtnClickCustom: async ({ action, data }) => {
        if (action === "approve" || action === "view") openDetail(data);
      },
      onRowClick: openDetail,
    }),
    [session, tabNav]
  );

  return (
    <PageList
      apiEntity={apiEntity}
      title="Approval Requests"
      formSchema={formSchema}
      hideActionButton
      showAddButton={false}
      preQuery={preQuery}
      stats={{ component: McaPageStats, props: { tab } }}
      header={{
        component: McaRequestTabs,
        props: { tab, setTab },
      }}
      tableSchema={tableSchema}
      emptyState={emptyState}
    />
  );
};

export default McaRequest;
